import {
  GetWebDashboard200Response,
  WebDashboardBlockChange,
  WebDashboardConfigFieldChange,
  WebDashboardEditDelta,
  WebDashboardContentConfig,
  WebDashboardSpecFieldChange,
} from "@coeff/api";

type ContentConfig = GetWebDashboard200Response["content_config"] | WebDashboardContentConfig;

// content_config-level fields the server accepts in a delta. Anything else changing at
// the config level makes the edit non-delta-representable and we fall back to the hard 409.
// Keep in sync with ALLOWED_CONFIG_DELTA_FIELDS in web_dashboard_merge.py
const CONFIG_DELTA_FIELDS: ReadonlySet<string> = new Set(["background", "mode", "theme_id"]);

// Derived fields the PUT nulls anyway — never part of a delta comparison.
const IGNORED_CONFIG_FIELDS = new Set(["content_html", "js_code", "content_spec"]);

const jsonEqual = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// The wire type for spec-field values is object-or-absent (see WebDashboardSpecFieldChange
// in spec.py); a changed field holding anything else makes the edit non-delta-representable.
const isObjectOrAbsent = (value: unknown): value is Record<string, unknown> | undefined =>
  value === undefined || value === null || (typeof value === "object" && !Array.isArray(value));

/**
 * Compute the per-slice change declaration for a manual save (ENG-7141 Approach A): which
 * blocks / content_spec fields / config fields this edit changed, each with the base value
 * it was computed from. On a concurrency-token mismatch the server verifies nobody else
 * touched those slices and rebases the edit onto the latest row instead of hard-failing.
 *
 * Returns undefined when the edit isn't delta-representable (a non-whitelisted config
 * field changed, or nothing changed) — the save then keeps today's hard-fail behavior.
 * Whole-snapshot flows (undo / revert-to-message) must NOT call this.
 */
export const buildEditDelta = (
  baseConfig: ContentConfig | undefined,
  newConfig: ContentConfig | undefined
): WebDashboardEditDelta | undefined => {
  if (!baseConfig || !newConfig) {
    return undefined;
  }

  const delta: WebDashboardEditDelta = {};

  // Config-level fields: whitelist only; any other config-level change is not mergeable.
  const configChanges: WebDashboardConfigFieldChange[] = [];

  for (const field of CONFIG_DELTA_FIELDS) {
    const baseValue = (baseConfig as Record<string, unknown>)[field];
    const newValue = (newConfig as Record<string, unknown>)[field];

    if (!jsonEqual(baseValue, newValue)) {
      configChanges.push({
        field,
        base_value: baseValue as string | undefined,
        new_value: newValue as string | undefined,
      });
    }
  }

  for (const field of Object.keys({ ...baseConfig, ...newConfig })) {
    if (IGNORED_CONFIG_FIELDS.has(field) || CONFIG_DELTA_FIELDS.has(field)) {
      continue;
    }
    const baseValue = (baseConfig as Record<string, unknown>)[field];
    const newValue = (newConfig as Record<string, unknown>)[field];

    if (!jsonEqual(baseValue, newValue)) {
      // A config-level change outside the whitelist (title, description, ...) — not
      // representable as a delta; let the save hard-fail on conflict instead.
      return undefined;
    }
  }

  const baseSpec = (baseConfig.content_spec ?? {}) as Record<string, unknown>;
  const newSpec = (newConfig.content_spec ?? {}) as Record<string, unknown>;

  // Blocks, keyed by block_id.
  const blocksById = (spec: Record<string, unknown>): Map<string, Record<string, unknown>> => {
    const result = new Map<string, Record<string, unknown>>();

    for (const block of (spec.blocks as Record<string, unknown>[] | undefined) ?? []) {
      const blockId = block?.block_id;
      if (typeof blockId === "string") {
        result.set(blockId, block);
      }
    }
    return result;
  };

  const baseBlocks = blocksById(baseSpec);
  const newBlocks = blocksById(newSpec);
  const blockChanges: WebDashboardBlockChange[] = [];

  for (const blockId of new Set([...baseBlocks.keys(), ...newBlocks.keys()])) {
    const baseBlock = baseBlocks.get(blockId);
    const newBlock = newBlocks.get(blockId);

    if (!jsonEqual(baseBlock, newBlock)) {
      blockChanges.push({ block_id: blockId, base_block: baseBlock, new_block: newBlock });
    }
  }

  // Non-block content_spec fields, whole-value.
  const specFieldChanges: WebDashboardSpecFieldChange[] = [];

  for (const field of new Set([...Object.keys(baseSpec), ...Object.keys(newSpec)])) {
    if (field === "blocks") {
      continue;
    }
    if (!jsonEqual(baseSpec[field], newSpec[field])) {
      if (!isObjectOrAbsent(baseSpec[field]) || !isObjectOrAbsent(newSpec[field])) {
        // Non-object spec field changed (title, slicers list, ...) — not representable
        // as a delta; keep the hard-fail behavior for this save.
        return undefined;
      }
      specFieldChanges.push({
        field,
        base_value: baseSpec[field] ?? undefined,
        new_value: newSpec[field] ?? undefined,
      });
    }
  }

  if (blockChanges.length > 0) {
    delta.block_changes = blockChanges;
  }
  if (specFieldChanges.length > 0) {
    delta.spec_field_changes = specFieldChanges;
  }
  if (configChanges.length > 0) {
    delta.config_field_changes = configChanges;
  }

  if (!delta.block_changes && !delta.spec_field_changes && !delta.config_field_changes) {
    return undefined;
  }
  return delta;
};
